import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { MapContainer, TileLayer, GeoJSON, Marker, Circle, ZoomControl } from 'react-leaflet';
import { CATEGORY_ICON } from '../shared/constants.js';
import L from 'leaflet';
import { useMap } from 'react-leaflet';

// ✅ Import componenti centralizzati
import POIMarkers from './POI/POIMarkers.jsx';
import OSMInfoDialog from './POI/OSMInfoDialog.jsx';
import POIEnrichmentForm from './POI/POIEnrichmentForm.jsx';

// Calcola distanza punto-linea (Haversine semplificato)
const getDistanceFromPointToLine = (point, lineCoords) => {
    let minDistance = Infinity;

    const [pLon, pLat] = point;

    lineCoords.forEach(coord => {
        const [lLon, lLat] = coord;
        const R = 6371; // Raggio Terra in km
        const dLat = (lLat - pLat) * Math.PI / 180;
        const dLon = (lLon - pLon) * Math.PI / 180;
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(pLat * Math.PI / 180) * Math.cos(lLat * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const distance = R * c;

        if (distance < minDistance) {
            minDistance = distance;
        }
    });

    return minDistance;
};

// Trova POI vicini a un percorso
const findPOIsNearRoute = (route, allFeatures, maxDistanceKm = 1) => {
    if (!route || !route.geometry || !allFeatures) return [];

    const routeCoords = route.geometry.type === 'MultiLineString'
        ? route.geometry.coordinates.flat()
        : route.geometry.coordinates;

    const nearbyPOIs = allFeatures
        .filter(f => f.geometry?.type === 'Point') // Solo POI
        .map(poi => {
            const distance = getDistanceFromPointToLine(poi.geometry.coordinates, routeCoords);
            return { poi, distance };
        })
        .filter(({ distance }) => distance <= maxDistanceKm)
        .sort((a, b) => a.distance - b.distance); // Ordina per distanza

    return nearbyPOIs;
};

// Hook per rilevare mobile
function useIsMobile() {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return isMobile;
}

// Cache per altitudini già calcolate
const elevationCache = new Map();

// Funzione per ottenere l'altitudine da coordinate
const getElevation = async (lat, lon) => {
    try {
        const response = await fetch(
            `${process.env.REACT_APP_API_URL_NODE}/api/weather/elevation?lat=${lat}&lon=${lon}`
        );

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Errore recupero elevazione');
        }

        const data = await response.json();
        console.log(`🏔️ Elevazione: ${data.elevation}m`);

        return data.elevation;

    } catch (error) {
        console.error('❌ Errore elevazione:', error);
        return null;
    }
};

// Componente Bussola - Solo se magnetometro disponibile E follow OFF
function Compass({ followUser, userPosition }) {
    const [heading, setHeading] = useState(0);
    const [isActive, setIsActive] = useState(false);
    const [checking, setChecking] = useState(true);

    useEffect(() => {
        // Controlla se DeviceOrientationEvent è supportato
        if (!window.DeviceOrientationEvent) {
            console.log('🧭 Bussola: DeviceOrientation non supportato (probabilmente desktop)');
            setChecking(false);
            return;
        }

        let hasReceivedData = false;
        let checkTimeout;

        const handleOrientation = (event) => {
            let newHeading = null;

            if (event.webkitCompassHeading !== undefined) {
                // iOS
                newHeading = event.webkitCompassHeading;
            } else if (event.alpha !== null) {
                // Android
                newHeading = 360 - event.alpha;
            }

            if (newHeading !== null) {
                if (!hasReceivedData) {
                    hasReceivedData = true;
                    setIsActive(true);
                    setChecking(false);
                    console.log('✅ Bussola attiva - magnetometro funzionante');
                }
                setHeading(newHeading);
            }
        };

        // Richiedi permessi su iOS 13+
        if (typeof DeviceOrientationEvent.requestPermission === 'function') {
            DeviceOrientationEvent.requestPermission()
                .then(response => {
                    if (response === 'granted') {
                        window.addEventListener('deviceorientation', handleOrientation);

                        // Timeout: se dopo 2 secondi non riceviamo dati, nessun magnetometro
                        checkTimeout = setTimeout(() => {
                            if (!hasReceivedData) {
                                console.log('⚠️ Bussola: nessun dato dal magnetometro (iOS)');
                                setIsActive(false);
                                setChecking(false);
                            }
                        }, 2000);
                    } else {
                        console.log('⚠️ Bussola: permesso negato (iOS)');
                        setChecking(false);
                    }
                })
                .catch(err => {
                    console.log('⚠️ Bussola: errore permesso (iOS)');
                    setChecking(false);
                });
        } else {
            // Non iOS, aggiungi listener direttamente
            window.addEventListener('deviceorientation', handleOrientation);

            // Timeout: se dopo 2 secondi non riceviamo dati, probabilmente desktop
            checkTimeout = setTimeout(() => {
                if (!hasReceivedData) {
                    console.log('⚠️ Bussola: nessun dato dal magnetometro (probabilmente desktop)');
                    setIsActive(false);
                    setChecking(false);
                }
            }, 2000);
        }

        return () => {
            window.removeEventListener('deviceorientation', handleOrientation);
            if (checkTimeout) clearTimeout(checkTimeout);
        };
    }, []);

    if (followUser || !userPosition || checking || !isActive) {
        return null;
    }

    // ✅ Mostra SOLO se il magnetometro funziona E follow è OFF
    return (
        <div style={{
            position: 'absolute',
            top: '80px',
            right: '10px',
            width: '60px',
            height: '60px',
            background: 'rgba(255, 255, 255, 0.95)',
            borderRadius: '50%',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid #2196F3',
            zIndex: 1000
        }}>
            {/* Ago della bussola - ruota in base all'orientamento */}
            <div style={{
                fontSize: '28px',
                transform: `rotate(${heading}deg)`,
                transition: 'transform 0.3s ease'
            }}>
                🧭
            </div>

            {/* Lettera N - sempre in alto (nord mappa) */}
            <div style={{
                position: 'absolute',
                top: '8px',
                fontSize: '10px',
                fontWeight: 'bold',
                color: '#d32f2f'
            }}>
                N
            </div>

            {/* Gradi - indica dove stai guardando */}
            <div style={{
                position: 'absolute',
                bottom: '4px',
                fontSize: '9px',
                fontWeight: 'bold',
                color: '#666'
            }}>
                {Math.round(heading)}°
            </div>
        </div>
    );
}

// Componente UserLocation per il modal
function UserLocationInModal({ followUser, onPositionUpdate, map }) {
    const [position, setPosition] = useState(null);
    const [accuracy, setAccuracy] = useState(0);
    const [error, setError] = useState(null);
    const watchIdRef = useRef(null);

    useEffect(() => {
        if (!navigator.geolocation) {
            console.error('❌ Geolocalizzazione non supportata dal browser');
            setError('Geolocalizzazione non supportata');
            return;
        }

        if (!map) {
            console.warn('⚠️ Mappa non ancora pronta');
            return;
        }

        console.log('🚀 Avvio tracking GPS...');

        const options = {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
        };

        const handleSuccess = (pos) => {
            console.log('✅ GPS Fix ricevuto:', {
                lat: pos.coords.latitude,
                lon: pos.coords.longitude,
                altitude: pos.coords.altitude,
                accuracy: pos.coords.accuracy
            });

            const newPosition = [pos.coords.latitude, pos.coords.longitude];
            setPosition(newPosition);
            setAccuracy(pos.coords.accuracy);
            setError(null);

            // Passa anche altitudine e timestamp
            if (onPositionUpdate) {
                const timestamp = new Date().toLocaleTimeString('it-IT', {
                    hour: '2-digit',
                    minute: '2-digit'
                });

                // Prova prima con altitudine GPS
                let gpsAltitude = pos.coords.altitude ? Math.round(pos.coords.altitude) : null;

                if (gpsAltitude) {
                    console.log('📡 Usando altitudine GPS:', gpsAltitude, 'm');
                    onPositionUpdate(newPosition, gpsAltitude, timestamp);
                } else {
                    console.log('⚠️ Altitudine GPS non disponibile, uso API...');
                    // Calcola altitudine da API
                    getElevation(pos.coords.latitude, pos.coords.longitude)
                        .then(apiAltitude => {
                            if (apiAltitude !== null) {
                                console.log('🏔️ Usando altitudine da API:', apiAltitude, 'm');
                                onPositionUpdate(newPosition, apiAltitude, timestamp);
                            } else {
                                console.log('❌ Altitudine non disponibile da nessuna fonte');
                                onPositionUpdate(newPosition, null, timestamp);
                            }
                        });
                }
            }
        };

        const handleError = (err) => {
            console.error('❌ Errore GPS:', {
                code: err.code,
                message: err.message,
                PERMISSION_DENIED: err.code === 1,
                POSITION_UNAVAILABLE: err.code === 2,
                TIMEOUT: err.code === 3
            });

            let errorMsg = 'Errore sconosciuto';

            switch (err.code) {
                case err.PERMISSION_DENIED:
                    errorMsg = 'Permesso GPS negato. Abilita la posizione nelle impostazioni del browser.';
                    console.error('🚫 PERMESSO GPS NEGATO - Vai nelle impostazioni del browser e abilita la posizione per questo sito');
                    break;
                case err.POSITION_UNAVAILABLE:
                    errorMsg = 'Posizione non disponibile.';
                    console.error('📡 POSIZIONE NON DISPONIBILE - Controlla che il GPS sia attivo sul dispositivo');
                    break;
                case err.TIMEOUT:
                    errorMsg = 'Timeout GPS. Riprova.';
                    console.error('⏱️ TIMEOUT GPS - Il dispositivo non riesce a ottenere la posizione');
                    break;
            }

            setError(errorMsg);
        };

        console.log('👀 Registrazione watchPosition...');
        watchIdRef.current = navigator.geolocation.watchPosition(
            handleSuccess,
            handleError,
            options
        );

        return () => {
            if (watchIdRef.current) {
                console.log('🛑 Stop tracking GPS');
                navigator.geolocation.clearWatch(watchIdRef.current);
            }
        };
    }, [map, followUser, onPositionUpdate]);

    if (error) {
        console.warn('⚠️ GPS in stato di errore:', error);
        return null;
    }

    if (!position) {
        console.log('⏳ In attesa del primo fix GPS...');
        return null;
    }

    const userIcon = L.divIcon({
        className: 'user-location-marker',
        html: `<div style="width: 20px; height: 20px; background: #4285F4; border: 3px solid white; border-radius: 50%; box-shadow: 0 2px 8px rgba(0,0,0,0.4);"></div>`,
        iconSize: [20, 20],
        iconAnchor: [10, 10]
    });

    return (
        <>
            <Circle
                center={position}
                radius={accuracy}
                pathOptions={{
                    color: '#4285F4',
                    fillColor: '#4285F4',
                    fillOpacity: 0.1,
                    weight: 1
                }}
            />
            <Marker position={position} icon={userIcon} />
        </>
    );
}

// Toast component
function Toast({ message, show, onClose }) {
    useEffect(() => {
        if (show) {
            const timer = setTimeout(onClose, 3000);
            return () => clearTimeout(timer);
        }
    }, [show, onClose]);

    if (!show) return null;

    return (
        <div style={{
            position: 'fixed',
            bottom: '80px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(0, 0, 0, 0.85)',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '24px',
            fontSize: '14px',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
        }}>
            {message}
        </div>
    );
}

// Bottom Sheet per "Invia Posizione"
function ShareLocationSheet({ isOpen, onClose, userPosition, altitude, routeName }) {
    const [toast, setToast] = useState({ show: false, message: '' });

    const showToast = (message) => {
        setToast({ show: true, message });
    };

    const formatCoordinates = (lat, lon) => {
        return `${lat.toFixed(5)}, ${lon.toFixed(5)}`;
    };

    const getTimestamp = () => {
        const now = new Date();
        return now.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
    };

    const getGoogleMapsLink = (lat, lon) => {
        return `https://www.google.com/maps?q=${lat.toFixed(5)},${lon.toFixed(5)}`;
    };

    const handleSharePosition = async () => {
        if (!userPosition) {
            alert('⚠️ Posizione non disponibile');
            return;
        }

        const [lat, lon] = userPosition;
        const timestamp = getTimestamp();
        const link = getGoogleMapsLink(lat, lon);

        let text = `Sono qui (agg. ${timestamp}):\nLat: ${lat.toFixed(5)}\nLon: ${lon.toFixed(5)}`;
        if (altitude) {
            text += `\nQuota: ${altitude} m`;
        }
        text += `\n\n${link}`;

        if (navigator.share) {
            try {
                await navigator.share({
                    title: 'La mia posizione',
                    text: text
                });
                onClose();
            } catch (err) {
                if (err.name !== 'AbortError') {
                    console.error('Errore condivisione:', err);
                    handleCopyLink();
                }
            }
        } else {
            handleCopyLink();
        }
    };

    const handleCopyCoordinates = async () => {
        if (!userPosition) {
            alert('⚠️ Posizione non disponibile');
            return;
        }

        const [lat, lon] = userPosition;
        const coords = formatCoordinates(lat, lon);

        try {
            await navigator.clipboard.writeText(coords);
            showToast('📋 Coordinate copiate');
            setTimeout(onClose, 1000);
        } catch (err) {
            console.error('Errore copia:', err);
            alert('❌ Errore durante la copia');
        }
    };

    const handleCopyLink = async () => {
        if (!userPosition) {
            alert('⚠️ Posizione non disponibile');
            return;
        }

        const [lat, lon] = userPosition;
        const link = getGoogleMapsLink(lat, lon);

        try {
            await navigator.clipboard.writeText(link);
            showToast('🔗 Link copiato');
            setTimeout(onClose, 1000);
        } catch (err) {
            console.error('Errore copia:', err);
            alert('❌ Errore durante la copia');
        }
    };

    if (!isOpen) return null;

    const positionAvailable = userPosition !== null;

    return (
        <>
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 3000,
                    animation: 'fadeIn 0.2s ease'
                }}
                onClick={onClose}
            />

            <div style={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                background: 'white',
                borderTopLeftRadius: '16px',
                borderTopRightRadius: '16px',
                boxShadow: '0 -2px 10px rgba(0,0,0,0.2)',
                zIndex: 3001,
                padding: '20px',
                animation: 'slideUp 0.3s ease',
                maxWidth: '600px',
                margin: '0 auto'
            }}>
                <div style={{
                    width: '40px',
                    height: '4px',
                    background: '#ddd',
                    borderRadius: '2px',
                    margin: '0 auto 16px',
                    cursor: 'pointer'
                }} onClick={onClose} />

                <h3 style={{
                    margin: '0 0 20px 0',
                    fontSize: '18px',
                    color: '#333'
                }}>
                    📍 Condividi la tua posizione
                </h3>

                {!positionAvailable ? (
                    <div style={{
                        padding: '20px',
                        background: '#fff3e0',
                        borderRadius: '8px',
                        color: '#e65100',
                        textAlign: 'center',
                        marginBottom: '16px'
                    }}>
                        ⚠️ Posizione non disponibile.<br />
                        Attiva il GPS e riprova.
                    </div>
                ) : (
                    <>
                        <button
                            onClick={handleSharePosition}
                            disabled={!positionAvailable}
                            style={{
                                width: '100%',
                                padding: '16px',
                                background: positionAvailable ? '#2196F3' : '#BDBDBD',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '16px',
                                fontWeight: '600',
                                cursor: positionAvailable ? 'pointer' : 'not-allowed',
                                marginBottom: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px'
                            }}
                        >
                            📤 Condividi posizione
                        </button>

                        <button
                            onClick={handleCopyCoordinates}
                            disabled={!positionAvailable}
                            style={{
                                width: '100%',
                                padding: '16px',
                                background: 'white',
                                color: positionAvailable ? '#333' : '#999',
                                border: positionAvailable ? '2px solid #ddd' : '2px solid #eee',
                                borderRadius: '8px',
                                fontSize: '16px',
                                fontWeight: '600',
                                cursor: positionAvailable ? 'pointer' : 'not-allowed',
                                marginBottom: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px'
                            }}
                        >
                            📋 Copia coordinate
                        </button>

                        <button
                            onClick={handleCopyLink}
                            disabled={!positionAvailable}
                            style={{
                                width: '100%',
                                padding: '16px',
                                background: 'white',
                                color: positionAvailable ? '#333' : '#999',
                                border: positionAvailable ? '2px solid #ddd' : '2px solid #eee',
                                borderRadius: '8px',
                                fontSize: '16px',
                                fontWeight: '600',
                                cursor: positionAvailable ? 'pointer' : 'not-allowed',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px'
                            }}
                        >
                            🔗 Copia link Google Maps
                        </button>
                    </>
                )}

                <div style={{
                    marginTop: '16px',
                    padding: '12px',
                    background: '#f5f5f5',
                    borderRadius: '8px',
                    fontSize: '12px',
                    color: '#666',
                    lineHeight: '1.5'
                }}>
                    ℹ️ Condividi la tua posizione con amici o familiari
                </div>
            </div>

            <Toast
                message={toast.message}
                show={toast.show}
                onClose={() => setToast({ show: false, message: '' })}
            />
        </>
    );
}

// Dialog SOS Emergenza
function SOSDialog({ isOpen, onClose, userPosition, altitude, routeName }) {
    const [toast, setToast] = useState({ show: false, message: '' });

    const showToast = (message) => {
        setToast({ show: true, message });
    };

    const getTimestamp = () => {
        const now = new Date();
        return now.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
    };

    const handleCall112 = () => {
        window.location.href = 'tel:112';
    };

    const handleCopyEmergencyInfo = async () => {
        if (!userPosition) {
            alert('⚠️ Posizione non disponibile');
            return;
        }

        const [lat, lon] = userPosition;
        const timestamp = getTimestamp();

        let text = `EMERGENZA IN MONTAGNA\n\n`;
        text += `Posizione GPS:\n${lat.toFixed(5)}, ${lon.toFixed(5)}\n\n`;
        if (altitude) {
            text += `Quota:\n${altitude} m\n\n`;
        }
        text += `Ora:\n${timestamp}\n\n`;
        if (routeName) {
            text += `Percorso:\n${routeName}`;
        }

        try {
            await navigator.clipboard.writeText(text);
            showToast('📋 Info di emergenza copiate');
        } catch (err) {
            console.error('Errore copia:', err);
            alert('❌ Errore durante la copia');
        }
    };

    if (!isOpen) return null;

    return (
        <>
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.7)',
                    zIndex: 3500,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px',
                    animation: 'fadeIn 0.2s ease'
                }}
                onClick={onClose}
            >
                <div
                    style={{
                        background: 'white',
                        borderRadius: '12px',
                        padding: '24px',
                        maxWidth: '400px',
                        width: '100%',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
                        animation: 'scaleIn 0.3s ease'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div style={{
                        fontSize: '48px',
                        textAlign: 'center',
                        marginBottom: '16px'
                    }}>
                        🆘
                    </div>

                    <h2 style={{
                        margin: '0 0 16px 0',
                        fontSize: '20px',
                        color: '#d32f2f',
                        textAlign: 'center',
                        fontWeight: 'bold'
                    }}>
                        Emergenza
                    </h2>

                    <div style={{
                        padding: '16px',
                        background: '#ffebee',
                        borderRadius: '8px',
                        marginBottom: '20px',
                        border: '2px solid #ef5350'
                    }}>
                        <p style={{
                            margin: 0,
                            fontSize: '14px',
                            lineHeight: '1.6',
                            color: '#c62828'
                        }}>
                            In caso di emergenza chiama il <strong>112</strong>.<br />
                            Puoi leggere all'operatore le informazioni qui sotto oppure incollarle in un messaggio.
                        </p>
                    </div>

                    <button
                        onClick={handleCall112}
                        style={{
                            width: '100%',
                            padding: '16px',
                            background: '#d32f2f',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '18px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            marginBottom: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                        }}
                    >
                        📞 Chiama 112
                    </button>

                    <button
                        onClick={handleCopyEmergencyInfo}
                        disabled={!userPosition}
                        style={{
                            width: '100%',
                            padding: '16px',
                            background: userPosition ? 'white' : '#f5f5f5',
                            color: userPosition ? '#333' : '#999',
                            border: userPosition ? '2px solid #ddd' : '2px solid #eee',
                            borderRadius: '8px',
                            fontSize: '16px',
                            fontWeight: '600',
                            cursor: userPosition ? 'pointer' : 'not-allowed',
                            marginBottom: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                        }}
                    >
                        📋 Copia info di emergenza
                    </button>

                    <button
                        onClick={onClose}
                        style={{
                            width: '100%',
                            padding: '12px',
                            background: 'transparent',
                            color: '#666',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '14px',
                            cursor: 'pointer'
                        }}
                    >
                        Annulla
                    </button>
                </div>
            </div>

            <Toast
                message={toast.message}
                show={toast.show}
                onClose={() => setToast({ show: false, message: '' })}
            />
        </>
    );
}

// Componente pannello controlli (riutilizzabile)
function ControlPanel({
    followUser,
    handleFollowToggle,
    downloadingMap,
    swStatus,
    handleDownloadMap,
    userPosition,
    userAltitude,
    lastUpdate,
    nearbyPOIs = [],
    onShareLocation,
    onSOS
}) {
    const [weather, setWeather] = useState(null);
    const [loadingWeather, setLoadingWeather] = useState(false);
    const [weatherLastUpdate, setWeatherLastUpdate] = useState(null);

    // Fetch meteo SOLO quando followUser è OFF
    useEffect(() => {
        if (!userPosition || followUser) {
            console.log('⏳ Meteo: in attesa (GPS off o follow attivo)');
            return;
        }

        const fetchWeather = async () => {
            setLoadingWeather(true);
            try {
                const [lat, lon] = userPosition;
                const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,precipitation,wind_speed_10m,weather_code&timezone=auto`;

                console.log('🌤️ Richiesta meteo per:', { lat, lon });

                const response = await fetch(url);
                const data = await response.json();

                console.log('📡 Risposta Open-Meteo:', data);

                if (data.current) {
                    const weatherData = {
                        temperature: Math.round(data.current.temperature_2m),
                        precipitation: data.current.precipitation || 0,
                        windSpeed: Math.round(data.current.wind_speed_10m),
                        weatherCode: data.current.weather_code
                    };
                    console.log('✅ Meteo caricato:', weatherData);
                    setWeather(weatherData);

                    // Salva timestamp aggiornamento
                    const timestamp = new Date().toLocaleTimeString('it-IT', {
                        hour: '2-digit',
                        minute: '2-digit'
                    });
                    setWeatherLastUpdate(timestamp);
                }
            } catch (error) {
                console.error('❌ Errore caricamento meteo:', error);
            } finally {
                setLoadingWeather(false);
            }
        };

        fetchWeather();
    }, [userPosition, followUser]);

    // Funzione per ottenere emoji meteo da weather code
    const getWeatherEmoji = (code) => {
        if (!code) return '🌤️';
        if (code === 0) return '☀️'; // Clear
        if (code <= 3) return '⛅'; // Partly cloudy
        if (code <= 48) return '🌫️'; // Fog
        if (code <= 67) return '🌧️'; // Rain
        if (code <= 77) return '🌨️'; // Snow
        if (code <= 82) return '🌦️'; // Rain showers
        if (code <= 86) return '🌨️'; // Snow showers
        return '⛈️'; // Thunderstorm
    };

    const getWeatherDescription = (code) => {
        if (!code) return 'Sereno';
        if (code === 0) return 'Sereno';
        if (code <= 3) return 'Parz. nuvoloso';
        if (code <= 48) return 'Nebbia';
        if (code <= 67) return 'Pioggia';
        if (code <= 77) return 'Neve';
        if (code <= 82) return 'Rovesci';
        if (code <= 86) return 'Nevicate';
        return 'Temporale';
    };

    return (
        <div style={{ padding: '20px' }}>
            <h3 style={{
                fontSize: '16px',
                marginTop: 0,
                marginBottom: '16px',
                color: '#333'
            }}>
                🧭 Navigazione
            </h3>

            {/* Switch Seguimi con hint modificato */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px',
                background: 'white',
                borderRadius: '8px',
                marginBottom: '12px',
                border: '2px solid #ddd'
            }}>
                <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '18px' }}>📍</span>
                        <strong style={{ fontSize: '14px', color: '#333' }}>
                            Segui la mia posizione
                        </strong>
                    </div>
                    <p style={{
                        margin: '4px 0 0 0',
                        fontSize: '11px',
                        color: '#666',
                        lineHeight: '1.4'
                    }}>
                        {followUser
                            ? 'La mappa si muove mentre cammini. Disattiva per vedere posizione e meteo'
                            : ''}
                    </p>
                </div>
                <label style={{
                    position: 'relative',
                    width: '51px',
                    height: '31px',
                    flexShrink: 0,
                    cursor: 'pointer'
                }}>
                    <input
                        type="checkbox"
                        checked={followUser}
                        onChange={(e) => handleFollowToggle(e.target.checked)}
                        style={{ display: 'none' }}
                    />
                    <div style={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        background: followUser ? '#2196F3' : '#ccc',
                        borderRadius: '31px',
                        transition: 'background 0.3s ease'
                    }} />
                    <div style={{
                        position: 'absolute',
                        width: '27px',
                        height: '27px',
                        background: 'white',
                        borderRadius: '50%',
                        top: '2px',
                        left: followUser ? '22px' : '2px',
                        transition: 'left 0.3s ease',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }} />
                </label>
            </div>

            {/* INFO GPS - SOLO quando followUser è OFF */}
            {userPosition && !followUser && (
                <div style={{
                    padding: '12px',
                    background: '#e3f2fd',
                    borderRadius: '8px',
                    border: '2px solid #2196F3',
                    marginBottom: '12px'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '8px'
                    }}>
                        <span style={{ fontSize: '18px' }}>📍</span>
                        <strong style={{ fontSize: '14px', color: '#1976D2' }}>
                            Posizione GPS
                        </strong>
                    </div>

                    <div style={{
                        fontSize: '12px',
                        color: '#333',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                        fontFamily: 'monospace'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span>🏔️</span>
                            <span><strong>{userAltitude || '---'} m</strong></span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span>📍</span>
                            <span>{userPosition[0].toFixed(5)}, {userPosition[1].toFixed(5)}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span>🕐</span>
                            <span>Agg. {lastUpdate}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* METEO - SOLO quando followUser è OFF */}
            {userPosition && !followUser && (
                <div style={{
                    padding: '12px',
                    background: '#fff8e1',
                    borderRadius: '8px',
                    border: '2px solid #ffb74d',
                    marginBottom: '12px'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '8px'
                    }}>
                        <span style={{ fontSize: '18px' }}>⚠️</span>
                        <strong style={{ fontSize: '14px', color: '#e65100' }}>
                            Meteo qui
                        </strong>
                    </div>

                    {loadingWeather ? (
                        <div style={{ fontSize: '12px', color: '#666', textAlign: 'center', padding: '8px' }}>
                            ⏳ Caricamento...
                        </div>
                    ) : weather ? (
                        <>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                fontSize: '13px',
                                color: '#333',
                                marginBottom: '6px'
                            }}>
                                <span style={{ fontSize: '32px' }}>
                                    {getWeatherEmoji(weather.weatherCode)}
                                </span>
                                <div>
                                    <div style={{ fontWeight: 'bold', fontSize: '16px' }}>
                                        {getWeatherDescription(weather.weatherCode)} • {weather.temperature}°C
                                    </div>
                                </div>
                            </div>
                            <div style={{
                                fontSize: '12px',
                                color: '#666',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '4px'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span>💨</span>
                                    <span>Vento {weather.windSpeed} km/h</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span>💧</span>
                                    <span>Pioggia {weather.precipitation.toFixed(1)} mm/h</span>
                                </div>
                                {/* Timestamp aggiornamento meteo */}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    marginTop: '4px',
                                    paddingTop: '4px',
                                    borderTop: '1px solid rgba(0,0,0,0.1)',
                                    fontFamily: 'monospace'
                                }}>
                                    <span>🕐</span>
                                    <span>Agg. {weatherLastUpdate}</span>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div style={{ fontSize: '11px', color: '#666', textAlign: 'center' }}>
                            In attesa dati meteo...
                        </div>
                    )}
                </div>
            )}

            {/* Bottone Scarica Offline */}
            <button
                onClick={handleDownloadMap}
                disabled={downloadingMap || swStatus !== 'available'}
                style={{
                    width: '100%',
                    padding: '12px',
                    background: downloadingMap ? '#9E9E9E' : (swStatus === 'available' ? '#2196F3' : '#BDBDBD'),
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: (downloadingMap || swStatus !== 'available') ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    marginBottom: '12px',
                    opacity: swStatus === 'available' ? 1 : 0.6
                }}
            >
                {downloadingMap ? (
                    <>
                        <div style={{
                            width: '14px',
                            height: '14px',
                            border: '2px solid white',
                            borderTopColor: 'transparent',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                        }} />
                        Scaricamento...
                    </>
                ) : (
                    <>📥 Salva offline</>
                )}
            </button>

            {swStatus !== 'available' && !downloadingMap && (
                <p style={{
                    margin: '0 0 12px 0',
                    fontSize: '11px',
                    color: '#f57c00',
                    textAlign: 'center'
                }}>
                    {swStatus === 'checking' && '⏳ Verifica in corso...'}
                    {swStatus === 'inactive' && '⚠️ Service Worker non attivo'}
                    {swStatus === 'unsupported' && '❌ Browser non supportato'}
                    {swStatus === 'error' && '❌ Errore SW'}
                </p>
            )}

            {downloadingMap && (
                <p style={{
                    margin: '0 0 12px 0',
                    fontSize: '11px',
                    color: '#666',
                    textAlign: 'center'
                }}>
                    Può richiedere fino a 1 minuto...
                </p>
            )}

            {/* Separatore */}
            <div style={{
                height: '1px',
                background: '#ddd',
                margin: '16px 0'
            }} />

            {/* Pulsante Invia Posizione */}
            <button
                onClick={onShareLocation}
                disabled={!userPosition}
                style={{
                    width: '100%',
                    padding: '14px',
                    background: userPosition ? '#2196F3' : '#BDBDBD',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: userPosition ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    marginBottom: '12px'
                }}
            >
                📍 Invia posizione
            </button>

            {/* Pulsante SOS */}
            <button
                onClick={onSOS}
                style={{
                    width: '100%',
                    padding: '14px',
                    background: '#d32f2f',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    marginBottom: '16px'
                }}
            >
                🆘 SOS - Emergenza
            </button>

            {/* Avviso sicurezza */}
            <div style={{
                padding: '12px',
                background: '#fff3e0',
                borderRadius: '8px',
                border: '1px solid #ffb74d',
                fontSize: '11px',
                lineHeight: '1.5',
                color: '#e65100'
            }}>
                <strong style={{ display: 'block', marginBottom: '6px' }}>
                    ⚠️ Prima di partire
                </strong>
                • Verifica il meteo<br />
                • Porta acqua e cibo<br />
                • Avvisa qualcuno<br />
                • Scarica la mappa offline
            </div>
        </div>
    );
}

export default function RouteNavigationModal({
    isOpen,
    selectedRoute,
    followUser,
    setFollowUser,
    downloadingMap,
    allPOIs = [],
    onClose,
    onDownloadMap
}) {
    const isMobile = useIsMobile();
    const [mapInstance, setMapInstance] = useState(null);
    const [userPosition, setUserPosition] = useState(null);
    const [userAltitude, setUserAltitude] = useState(null);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [swStatus, setSwStatus] = useState('checking');
    const [sheetExpanded, setSheetExpanded] = useState(false);
    const [touchStart, setTouchStart] = useState(null);

    // Stati per le funzionalità
    const [shareSheetOpen, setShareSheetOpen] = useState(false);
    const [sosDialogOpen, setSOSDialogOpen] = useState(false);

    // ✅ Stati per POI
    const [osmInfoDialogOpen, setOsmInfoDialogOpen] = useState(false);
    const [enrichmentFormOpen, setEnrichmentFormOpen] = useState(false);
    const [selectedPOI, setSelectedPOI] = useState(null);

    const props = selectedRoute?.properties || {};
    const coords = selectedRoute?.geometry?.coordinates;

    let bounds = null;
    let startCoords = null;

    const nearbyPOIs = useMemo(() => {
        if (!selectedRoute || !allPOIs || allPOIs.length === 0) return [];
        return findPOIsNearRoute(selectedRoute, allPOIs, 1); // Max 1km di distanza
    }, [selectedRoute, allPOIs]);

    if (coords && coords.length > 0) {
        if (selectedRoute.geometry.type === 'MultiLineString') {
            const allCoords = coords.flat();
            bounds = L.latLngBounds(allCoords.map(coord => [coord[1], coord[0]]));
            startCoords = coords[0][0];
        } else {
            bounds = L.latLngBounds(coords.map(coord => [coord[1], coord[0]]));
            startCoords = coords[0];
        }
    }

    const center = bounds ? bounds.getCenter() : [45.5, 11.5];

    const routeStyle = {
        color: '#4CAF50',
        weight: 4,
        opacity: 0.8,
        lineCap: 'round',
        lineJoin: 'round'
    };

    // Calcola altitudine del punto di partenza del percorso
    useEffect(() => {
        if (!startCoords) return;

        const [lon, lat] = startCoords;
        console.log('🏔️ Calcolo altitudine punto di partenza percorso...');

        getElevation(lat, lon)
            .then(elevation => {
                if (elevation !== null) {
                    console.log('✅ Altitudine punto partenza:', elevation, 'm');
                }
            });
    }, [startCoords]);

    // Verifica Service Worker
    useEffect(() => {
        const checkServiceWorker = async () => {
            console.log('🔍 Verifica Service Worker...');

            if (!('serviceWorker' in navigator)) {
                console.error('❌ Service Worker NON supportato dal browser');
                setSwStatus('unsupported');
                return;
            }

            console.log('✅ Service Worker supportato dal browser');

            try {
                const registration = await navigator.serviceWorker.getRegistration();
                console.log('📋 Service Worker registration:', registration);

                if (registration && registration.active) {
                    console.log('✅ Service Worker ATTIVO:', registration.active);
                    setSwStatus('available');
                } else if (registration && registration.installing) {
                    console.log('⏳ Service Worker in INSTALLAZIONE...');
                    setSwStatus('inactive');
                } else if (registration && registration.waiting) {
                    console.log('⏳ Service Worker in ATTESA...');
                    setSwStatus('inactive');
                } else {
                    console.warn('⚠️ Service Worker NON registrato');
                    console.log('💡 Controlla che serviceWorkerRegistration.register() sia chiamato in src/index.js');
                    setSwStatus('inactive');
                }
            } catch (error) {
                console.error('❌ Errore verifica SW:', error);
                setSwStatus('error');
            }
        };

        if (isOpen) {
            checkServiceWorker();
        }
    }, [isOpen]);

    // Fit bounds quando la mappa viene creata e cleanup alla chiusura
    useEffect(() => {
        if (isOpen && mapInstance && bounds) {
            const timeoutId = setTimeout(() => {
                try {
                    if (mapInstance.getContainer && mapInstance.getContainer().parentNode) {
                        mapInstance.fitBounds(bounds, { padding: [50, 50] });
                    }
                } catch (error) {
                    console.warn('⚠️ Errore fitBounds:', error);
                }
            }, 100);

            return () => clearTimeout(timeoutId);
        }

        if (!isOpen && mapInstance) {
            setMapInstance(null);
        }
    }, [mapInstance, bounds, isOpen]);
    const handleCloseOSMInfo = useCallback(() => {
        setOsmInfoDialogOpen(false);
    }, []);
    const handleCloseEnrichmentForm = useCallback(() => {
        setEnrichmentFormOpen(false);
    }, []);
    // Handler per attivare GPS
    const handleFollowToggle = (checked) => {
        if (checked && !navigator.geolocation) {
            alert('❌ Il tuo browser non supporta la geolocalizzazione');
            return;
        }

        if (checked) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setFollowUser(true);
                },
                (error) => {
                    console.error('❌ Permesso GPS negato:', error);
                    alert('⚠️ Devi abilitare i permessi GPS per usare questa funzione');
                },
                { enableHighAccuracy: true, timeout: 10000 }
            );
        } else {
            setFollowUser(false);
        }
    };

    // Handler download mappa offline
    const handleDownloadMap = () => {
        if (swStatus === 'unsupported') {
            alert('❌ Il tuo browser non supporta i Service Worker.\n\nProva con Chrome, Firefox o Edge.');
            return;
        }

        if (swStatus === 'inactive' || swStatus === 'error') {
            alert('⚠️ Service Worker non disponibile.\n\nRicarica la pagina e riprova.');
            return;
        }

        if (swStatus === 'checking') {
            alert('⏳ Verifica del Service Worker in corso...\n\nAttendi qualche secondo e riprova.');
            return;
        }

        onDownloadMap();
    };

    // ✅ Handlers per POI
    const handleShowOSMInfo = (poi) => {
        setSelectedPOI(poi);
        setOsmInfoDialogOpen(true);
    };

    const handleEnrichPOI = (poi) => {
        setSelectedPOI(poi);
        setEnrichmentFormOpen(true);
    };

    // Touch handlers per bottom sheet
    const handleTouchStart = (e) => {
        setTouchStart(e.touches[0].clientY);
    };

    const handleTouchMove = (e) => {
        if (!touchStart) return;

        const currentTouch = e.touches[0].clientY;
        const diff = touchStart - currentTouch;

        if (diff > 50 && !sheetExpanded) {
            setSheetExpanded(true);
            setTouchStart(null);
        } else if (diff < -50 && sheetExpanded) {
            setSheetExpanded(false);
            setTouchStart(null);
        }
    };

    const handleTouchEnd = () => {
        setTouchStart(null);
    };

    if (!isOpen || !selectedRoute) return null;

    // ============================================
    // LAYOUT MOBILE - Bottom Sheet
    // ============================================
    if (isMobile) {
        return (
            <>
                {/* Mappa a schermo intero */}
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 2500,
                    background: 'white'
                }}>
                    <MapContainer
                        ref={(map) => {
                            if (map && !mapInstance) {
                                setMapInstance(map);
                            }
                        }}
                        center={center}
                        zoom={13}
                        maxBounds={bounds ? bounds.pad(0.5) : undefined}
                        maxBoundsViscosity={0.8}
                        style={{ height: '100%', width: '100%' }}
                    >
                        <TileLayer
                            url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
                            attribution="© OpenTopoMap contributors"
                            maxZoom={17}
                            bounds={bounds ? bounds.pad(0.5) : undefined}
                        />

                        <GeoJSON data={selectedRoute} style={routeStyle} />

                        {startCoords && (
                            <Marker
                                position={[startCoords[1], startCoords[0]]}
                                icon={L.divIcon({
                                    className: 'start-marker',
                                    html: `<div style="background: #4CAF50; color: white; width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 18px; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3);">🚶</div>`,
                                    iconSize: [30, 30],
                                    iconAnchor: [15, 15]
                                })}
                            />
                        )}

                        {/* ✅ Componente POIMarkers centralizzato */}
                        {nearbyPOIs && nearbyPOIs.length > 0 && (
                            <POIMarkers
                                pois={nearbyPOIs}
                                onShowOSMInfo={handleShowOSMInfo}
                                onEnrichPOI={handleEnrichPOI}
                            />
                        )}

                        {followUser && mapInstance && (
                            <UserLocationInModal
                                followUser={followUser}
                                onPositionUpdate={(pos, alt, time) => {
                                    setUserPosition(pos);
                                    setUserAltitude(alt);
                                    setLastUpdate(time);
                                }}
                                map={mapInstance}
                            />
                        )}
                    </MapContainer>

                    {/* Bussola in alto a destra */}
                    <Compass followUser={followUser} userPosition={userPosition} />

                    {/* Bottone chiudi in alto a sinistra */}
                    <button
                        onClick={onClose}
                        style={{
                            position: 'absolute',
                            top: '16px',
                            left: '16px',
                            width: '44px',
                            height: '44px',
                            background: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            fontSize: '20px',
                            cursor: 'pointer',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                            zIndex: 1000,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        ←
                    </button>
                </div>

                {/* Bottom Sheet */}
                <div
                    style={{
                        position: 'fixed',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background: 'white',
                        borderTopLeftRadius: '16px',
                        borderTopRightRadius: '16px',
                        boxShadow: '0 -2px 10px rgba(0,0,0,0.2)',
                        zIndex: 2501,
                        transition: 'transform 0.3s ease',
                        transform: sheetExpanded ? 'translateY(0)' : 'translateY(calc(100% - 140px))',
                        maxHeight: '80vh',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                >
                    <div
                        onClick={() => setSheetExpanded(!sheetExpanded)}
                        style={{
                            width: '40px',
                            height: '4px',
                            background: '#ddd',
                            borderRadius: '2px',
                            margin: '12px auto 8px',
                            cursor: 'pointer'
                        }}
                    />

                    <div style={{
                        padding: '12px 16px',
                        borderBottom: '1px solid #eee'
                    }}>
                        <h2 style={{ margin: 0, fontSize: '18px', color: '#333' }}>
                            🗺️ {props.name || 'Percorso di trekking'}
                        </h2>
                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            marginTop: '6px',
                            fontSize: '12px',
                            color: '#666',
                            flexWrap: 'wrap'
                        }}>
                            {props.lengthKm && <span>📏 {props.lengthKm} km</span>}
                            {props.ascentMeters && <span>⛰️ {props.ascentMeters} m</span>}
                            {props.totalDurationMin && <span>⏱️ {props.totalDurationMin} min</span>}
                        </div>
                    </div>

                    <div style={{
                        flex: 1,
                        overflowY: 'auto',
                        WebkitOverflowScrolling: 'touch'
                    }}>
                        {props.description && (
                            <div style={{
                                padding: '12px 16px',
                                background: '#f9f9f9',
                                borderBottom: '1px solid #eee',
                                fontSize: '13px',
                                lineHeight: '1.6'
                            }}>
                                <strong>ℹ️ Descrizione:</strong><br />
                                {props.description}
                            </div>
                        )}

                        <ControlPanel
                            followUser={followUser}
                            handleFollowToggle={handleFollowToggle}
                            downloadingMap={downloadingMap}
                            nearbyPOIs={nearbyPOIs}
                            swStatus={swStatus}
                            handleDownloadMap={handleDownloadMap}
                            userPosition={userPosition}
                            userAltitude={userAltitude}
                            lastUpdate={lastUpdate}
                            onShareLocation={() => setShareSheetOpen(true)}
                            onSOS={() => setSOSDialogOpen(true)}
                        />
                    </div>
                </div>

                {/* ✅ Dialog Info OSM centralizzato */}
                {/* ✅ Dialog Info OSM centralizzato */}
                {osmInfoDialogOpen && selectedPOI && (
                    <OSMInfoDialog
                        onClose={handleCloseOSMInfo}
                        poi={selectedPOI}
                    />
                )}

                {/* ✅ Form Enrichment centralizzato */}
                {enrichmentFormOpen && selectedPOI && <POIEnrichmentForm
                    onClose={handleCloseEnrichmentForm}
                    poi={selectedPOI}
                />}

                {/* Share Location Sheet */}
                <ShareLocationSheet
                    isOpen={shareSheetOpen}
                    onClose={() => setShareSheetOpen(false)}
                    userPosition={userPosition}
                    altitude={userAltitude}
                    routeName={props.name}
                />

                {/* SOS Dialog */}
                <SOSDialog
                    isOpen={sosDialogOpen}
                    onClose={() => setSOSDialogOpen(false)}
                    userPosition={userPosition}
                    altitude={userAltitude}
                    routeName={props.name}
                />

                <style>{`
                    @keyframes spin {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                    @keyframes fadeIn {
                        from { opacity: 0; }
                        to { opacity: 1; }
                    }
                    @keyframes slideUp {
                        from { transform: translateY(100%); }
                        to { transform: translateY(0); }
                    }
                    @keyframes scaleIn {
                        from { transform: scale(0.9); opacity: 0; }
                        to { transform: scale(1); opacity: 1; }
                    }
                `}</style>
            </>
        );
    }

    // ============================================
    // LAYOUT DESKTOP - Modal centrato
    // ============================================
    return (
        <>
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.85)',
                    zIndex: 2000,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px',
                    animation: 'fadeIn 0.3s ease'
                }}
                onClick={onClose}
            >
                <div
                    style={{
                        background: 'white',
                        borderRadius: '12px',
                        width: '95%',
                        maxWidth: '1200px',
                        height: '90vh',
                        maxHeight: '90vh',
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
                        overflow: 'hidden'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div style={{
                        padding: '16px 20px',
                        borderBottom: '2px solid #4CAF50',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        background: 'white',
                        flexShrink: 0
                    }}>
                        <div style={{ flex: 1 }}>
                            <h2 style={{ margin: 0, fontSize: '20px', color: '#333' }}>
                                🗺️ {props.name || 'Percorso di trekking'}
                            </h2>
                            <div style={{
                                display: 'flex',
                                gap: '16px',
                                marginTop: '8px',
                                fontSize: '13px',
                                color: '#666',
                                flexWrap: 'wrap'
                            }}>
                                {props.lengthKm && <span>📏 {props.lengthKm} km</span>}
                                {props.ascentMeters && <span>⛰️ {props.ascentMeters} m</span>}
                                {props.totalDurationMin && <span>⏱️ {props.totalDurationMin} min</span>}
                                {props.difficulty && <span>🎯 {props.difficulty}</span>}
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            style={{
                                background: 'none',
                                border: 'none',
                                fontSize: '28px',
                                cursor: 'pointer',
                                color: '#666',
                                padding: '0',
                                lineHeight: '1',
                                minWidth: '32px',
                                minHeight: '32px',
                                marginLeft: '16px'
                            }}
                        >
                            ×
                        </button>
                    </div>

                    <div style={{
                        flex: 1,
                        display: 'flex',
                        overflow: 'hidden',
                        position: 'relative'
                    }}>
                        <div style={{ flex: 1, position: 'relative' }}>
                            <MapContainer
                                ref={(map) => {
                                    if (map && !mapInstance) {
                                        setMapInstance(map);
                                    }
                                }}
                                center={center}
                                zoom={13}
                                zoomControl={false}
                                maxBounds={bounds ? bounds.pad(0.5) : undefined}
                                maxBoundsViscosity={0.8}
                                style={{ height: '100%', width: '100%' }}
                            >
                                <TileLayer
                                    url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
                                    attribution="© OpenTopoMap contributors"
                                    maxZoom={17}
                                    bounds={bounds ? bounds.pad(0.5) : undefined}
                                />
                                <ZoomControl position="topright" />

                                <GeoJSON data={selectedRoute} style={routeStyle} />

                                {startCoords && (
                                    <Marker
                                        position={[startCoords[1], startCoords[0]]}
                                        icon={L.divIcon({
                                            className: 'start-marker',
                                            html: `<div style="background: #4CAF50; color: white; width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 18px; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3);">🚶</div>`,
                                            iconSize: [30, 30],
                                            iconAnchor: [15, 15]
                                        })}
                                    />
                                )}

                                {/* ✅ Componente POIMarkers centralizzato */}
                                {nearbyPOIs && nearbyPOIs.length > 0 && (
                                    <POIMarkers
                                        pois={nearbyPOIs}
                                        onShowOSMInfo={handleShowOSMInfo}
                                        onEnrichPOI={handleEnrichPOI}
                                    />
                                )}

                                {followUser && mapInstance && (
                                    <UserLocationInModal
                                        followUser={followUser}
                                        onPositionUpdate={(pos, alt, time) => {
                                            setUserPosition(pos);
                                            setUserAltitude(alt);
                                            setLastUpdate(time);
                                        }}
                                        map={mapInstance}
                                    />
                                )}
                            </MapContainer>

                            {/* Bussola in alto a destra */}
                            <Compass followUser={followUser} userPosition={userPosition} />

                            {props.description && (
                                <div style={{
                                    position: 'absolute',
                                    top: '10px',
                                    left: '10px',
                                    right: '10px',
                                    background: 'rgba(255, 255, 255, 0.95)',
                                    padding: '12px',
                                    borderRadius: '8px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                    fontSize: '13px',
                                    lineHeight: '1.6',
                                    maxWidth: '400px',
                                    zIndex: 1000,
                                    pointerEvents: 'none'
                                }}>
                                    <strong>ℹ️ Descrizione:</strong><br />
                                    {props.description}
                                </div>
                            )}
                        </div>

                        <div style={{
                            width: '280px',
                            background: '#f9f9f9',
                            borderLeft: '1px solid #ddd',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'auto'
                        }}>
                            <ControlPanel
                                followUser={followUser}
                                handleFollowToggle={handleFollowToggle}
                                downloadingMap={downloadingMap}
                                nearbyPOIs={nearbyPOIs}
                                swStatus={swStatus}
                                handleDownloadMap={handleDownloadMap}
                                userPosition={userPosition}
                                userAltitude={userAltitude}
                                lastUpdate={lastUpdate}
                                onShareLocation={() => setShareSheetOpen(true)}
                                onSOS={() => setSOSDialogOpen(true)}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* ✅ Dialog Info OSM centralizzato */}
            {osmInfoDialogOpen && selectedPOI && (
                <OSMInfoDialog
                    onClose={() => setOsmInfoDialogOpen(false)}
                    poi={selectedPOI}
                />
            )}

            {/* Share Location Sheet */}
            <ShareLocationSheet
                isOpen={shareSheetOpen}
                onClose={() => setShareSheetOpen(false)}
                userPosition={userPosition}
                altitude={userAltitude}
                routeName={props.name}
            />

            {/* SOS Dialog */}
            <SOSDialog
                isOpen={sosDialogOpen}
                onClose={() => setSOSDialogOpen(false)}
                userPosition={userPosition}
                altitude={userAltitude}
                routeName={props.name}
            />

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                
                @keyframes slideUp {
                    from { transform: translateY(100%); }
                    to { transform: translateY(0); }
                }
                
                @keyframes scaleIn {
                    from { transform: scale(0.9); opacity: 0; }
                    to { transform: scale(1); opacity: 1; }
                }
            `}</style>
        </>
    );
}